import React, {
  createContext,
  useContext,
  useState,
  FC,
  useEffect,
} from 'react';
import styles from './styles.module.css';
import { motion, AnimatePresence } from 'framer-motion';
import { withRouter } from 'react-router';
import classNames from 'classnames';
import { Variant, Asset } from '../Asset';
import { reportError, reportEvent } from 'Shared/analytics';

export type ToastOptions = {
  message: string;
  type?: 'success' | 'error' | 'warning';
  position?: 'top' | 'bottom';
  width?: 'regular' | 'full';
  withSecondaryNav?: boolean;
  withFooter?: boolean;
  icon?: Variant;
  context?: Record<string, string>;
  persistOnNavigation?: boolean;
};

type ToastContextType = {
  toast: (toastOptions: ToastOptions) => void;
};

const ToastContext = createContext<ToastContextType>({
  toast: () => {},
});

export const useToast = () => useContext(ToastContext);

const Toast: FC<{ onClick: () => void } & ToastOptions> = ({
  onClick,
  message,
  position = 'bottom',
  type = 'success',
  width = 'regular',
  withFooter = true,
  withSecondaryNav = true,
  icon,
}) => (
  <div
    className={classNames(
      'fs-mask',
      styles.toastContainer,
      styles[position],
      styles[width],
      {
        [styles.withSecondaryNav]: withSecondaryNav,
      },
      {
        [styles.withFooter]: withFooter,
      }
    )}
    data-testid="toast"
  >
    <motion.div
      onClick={onClick}
      initial={{ opacity: 0, y: 10, scale: 0.3 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{
        opacity: 0,
        scale: 0.7,
        y: 10,
        transition: { duration: 0.2 },
      }}
      className={classNames(styles.toast, styles[type], styles[width])}
    >
      {icon ? <Asset variant={icon} /> : null} {message}
    </motion.div>
  </div>
);

export const ToastProvider = withRouter(({ children, location }) => {
  const [toast, setToast] = useState<ToastOptions | null>(null);
  const open = (toastOptions: ToastOptions) => {
    setToast(toastOptions);
  };
  const close = () => {
    setToast(null);
  };

  useEffect(() => {
    let id: NodeJS.Timeout;
    if (toast) {
      if (toast.type === 'error') {
        const err = Error(`Toast error: ${toast.message}`);
        reportError(err, 'global-toast-error-displayed', toast.context);
        reportEvent('global-toast-error-displayed', {
          message: toast.message,
          ...toast.context,
        });
      }
      id = setInterval(close, 8000);
    }
    return () => clearInterval(id);
  }, [toast]);

  useEffect(() => {
    if (!toast?.persistOnNavigation) {
      close();
    }
    // only fire this when location changes, not toast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <ToastContext.Provider value={{ toast: open }}>
      <AnimatePresence initial={false}>
        {toast && (
          <Toast
            onClick={close}
            message={toast.message}
            position={toast.position}
            width={toast.width}
            type={toast.type}
            withFooter={toast.withFooter}
            withSecondaryNav={toast.withSecondaryNav}
            persistOnNavigation={toast.persistOnNavigation}
            icon={toast.icon}
          />
        )}
      </AnimatePresence>
      {children}
    </ToastContext.Provider>
  );
});
